import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from 'src/components/Icon';

/**
 * Global toast manager. Stack of transient notifications anchored to the
 * top-right of the viewport. Replaces ad-hoc `<Notification>` blocks
 * sprinkled inside views — those are still fine for INLINE feedback (e.g.
 * a form-level validation summary), but page-level async results (saved,
 * failed, copied to clipboard) belong here.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Network error', { action: { label: 'Retry', onClick: retry } });
 *   toast.info('Copied to clipboard');
 *
 * The shortcut helpers (`.success`, `.error`, `.warning`, `.info`) auto-
 * pick an icon and colour; `.show({...})` is the escape hatch for custom
 * tones or longer-lived toasts.
 *
 * Each toast auto-dismisses after `duration` ms (default 4500) unless an
 * action is present, in which case the duration extends to 8000 so the
 * user actually has time to click. Pass `duration: 0` to make it sticky.
 *
 * Mount `<ToastProvider>` once near the root of the app (above any
 * component that needs to call `useToast`). The component itself renders
 * a portal — it doesn't take layout space.
 */

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: React.ReactNode;
  onClick: () => void;
}

export interface ToastOptions {
  id?: string;
  kind?: ToastKind;
  title?: React.ReactNode;
  message?: React.ReactNode;
  duration?: number;
  action?: ToastAction;
}

interface ToastItem extends Required<Omit<ToastOptions, 'action' | 'title' | 'id'>> {
  id: string;
  title?: React.ReactNode;
  action?: ToastAction;
}

interface ToastApi {
  show: (opts: ToastOptions) => string;
  success: (message: React.ReactNode, opts?: Omit<ToastOptions, 'kind' | 'message'>) => string;
  error: (message: React.ReactNode, opts?: Omit<ToastOptions, 'kind' | 'message'>) => string;
  warning: (message: React.ReactNode, opts?: Omit<ToastOptions, 'kind' | 'message'>) => string;
  info: (message: React.ReactNode, opts?: Omit<ToastOptions, 'kind' | 'message'>) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const KIND_STYLES: Record<ToastKind, { ring: string; icon: string; iconClass: string }> = {
  success: { ring: 'ring-emerald-500/30 border-emerald-500/30', icon: 'CheckCircle', iconClass: 'text-emerald-500' },
  error: { ring: 'ring-red-500/30 border-red-500/30', icon: 'WarningCircle', iconClass: 'text-danger' },
  warning: { ring: 'ring-amber-500/30 border-amber-500/30', icon: 'Warning', iconClass: 'text-amber-500' },
  info: { ring: 'ring-sky-500/30 border-sky-500/30', icon: 'Info', iconClass: 'text-sky-500' },
};

function randomId() {
  return Math.random()
    .toString(36)
    .slice(2, 10);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const dismiss = useCallback((id: string) => {
    setToasts(cur => cur.filter(t => t.id !== id));
    if (timeouts.current[id]) {
      clearTimeout(timeouts.current[id]);
      delete timeouts.current[id];
    }
  }, []);

  const scheduleDismiss = useCallback(
    (id: string, ms: number) => {
      if (ms <= 0) return; // sticky
      if (timeouts.current[id]) clearTimeout(timeouts.current[id]);
      timeouts.current[id] = setTimeout(() => dismiss(id), ms);
    },
    [dismiss]
  );

  const show = useCallback(
    (opts: ToastOptions) => {
      const id = opts.id ?? randomId();
      const kind: ToastKind = opts.kind ?? 'info';
      const duration = typeof opts.duration === 'number' ? opts.duration : opts.action ? 8000 : 4500;

      setToasts(cur => {
        const next: ToastItem = {
          id,
          kind,
          title: opts.title,
          message: opts.message ?? '',
          duration,
          action: opts.action,
        };
        const replaced = cur.filter(t => t.id !== id);
        return [...replaced, next];
      });
      scheduleDismiss(id, duration);
      return id;
    },
    [scheduleDismiss]
  );

  // Tear down timers if the provider unmounts mid-life.
  useEffect(
    () => () => {
      Object.values(timeouts.current).forEach(clearTimeout);
      timeouts.current = {};
    },
    []
  );

  const api: ToastApi = useMemo(
    () => ({
      show,
      success: (message, opts = {}) => show({ ...opts, kind: 'success', message }),
      error: (message, opts = {}) => show({ ...opts, kind: 'error', message }),
      warning: (message, opts = {}) => show({ ...opts, kind: 'warning', message }),
      info: (message, opts = {}) => show({ ...opts, kind: 'info', message }),
      dismiss,
    }),
    [show, dismiss]
  );

  // Expose to non-React callers (e.g. global `fetch` error handler) via a
  // window-level escape hatch. Keeps the React API as the canonical one
  // but means a one-off util can do `window.__uweToast?.error(...)` if it
  // doesn't have a hook context to live in.
  useEffect(() => {
    (window as any).__uweToast = api;
    return () => {
      if ((window as any).__uweToast === api) delete (window as any).__uweToast;
    };
  }, [api]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      className="fixed top-4 right-4 z-[3000] flex flex-col gap-2 max-w-sm w-[min(92vw,24rem)] pointer-events-none"
    >
      {toasts.map(t => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const styles = KIND_STYLES[toast.kind];
  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 p-3 pr-2 rounded-xl border ring-1 shadow-lg backdrop-blur-md bg-white/95 dark:bg-zinc-900/95 ${styles.ring} animate-[toast-in_220ms_var(--ease-emphasis)]`}
    >
      <Icon icon={styles.icon} className={`${styles.iconClass} text-base mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        {toast.title ? (
          <div className="t-h5 m-0 mb-0.5 normal-case tracking-normal text-[13px] font-semibold theme-text-primary">{toast.title}</div>
        ) : null}
        <div className="t-body-sm text-[13px] theme-text-secondary">{toast.message}</div>
        {toast.action ? (
          <button
            type="button"
            onClick={() => {
              toast.action!.onClick();
              onDismiss();
            }}
            className="mt-1.5 text-[12px] font-semibold theme-accent-text hover:underline"
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-white/5 transition-colors"
      >
        <Icon icon="X" weight="regular" className="text-xs" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Provider not mounted — return a no-op API that logs so callers don't
    // crash. Easier debugging than a hard throw when someone forgets to
    // wrap their tree in <ToastProvider>.
    const warn = (m: any) => console.warn('[Toast] ToastProvider not mounted; dropping toast:', m);
    return {
      show: o => {
        warn(o.message);
        return '';
      },
      success: m => {
        warn(m);
        return '';
      },
      error: m => {
        warn(m);
        return '';
      },
      warning: m => {
        warn(m);
        return '';
      },
      info: m => {
        warn(m);
        return '';
      },
      dismiss: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
